import "core-js/modules/es.array.push.js";
import "bezier-easing";
import { daysUntilNow } from "../../util/dates.mjs";
import "js-cookie";
const toTitleCase = (string) => string.toLowerCase().replace(/\w*/g, (txt) => `${txt.charAt(0).toUpperCase()}${txt.substr(1)}`);
const baseGetters = {
  /**
   * getters.appPropsMap
   *
   * Maps long app prop names to shorter, js friendly names. Used mostly in actions.
   * !Note: not on this list but used elsewhere and must be kept:
   *  ['frontend.dev.toggles'].logToConsole is used directly in logger.js
   *  productPage.dev.tools used in product-site - /src/main/js/pages/details/entry.js
   *
   * @param {object<*>} state.appProps: map of data returned from app props.
   *  Values can be of any type (object, array, string, boolean)
   */
  appPropsMap({
    appProps
  }) {
    return {
      abTestNames: appProps["frontend.abtest"],
      apiKeys: appProps["frontend.api.keys"],
      assets: appProps["frontend.static.assets"],
      contentstackHashes: appProps["frontend.contentstack.hashes"],
      devToggles: appProps["frontend.dev.toggles"],
      endpoints: appProps["frontend.endpoints"],
      launchToggles: appProps["frontend.launch.toggles"],
      recsPlacements: appProps["frontend.recommendations.placements"],
      scriptPaths: appProps["frontend.script.paths"],
      showPriceOfferMessages: appProps["frontend.priceOffer.toggles"],
      textStrings: appProps["frontend.strings"],
      triageToggles: appProps["frontend.triage.toggles"],
      mabcEarnPeriodToggle: appProps["frontend.mabc.enabled"]
    };
  },
  /**
   * getters.computedSelectedSku
   *
   * For most products, calculated based on the selected size and color.
   * For size/color/price dropdown styles, size data includes sku reference.
   *
   * @params from state.product:
   * @param {Boolean} displayOptions.showSkuDropDown if true, product uses sku dropdown
   * @param {Object<Object>} bySku: skuId mapped to sku properties
   * @param {Object} bySize: map of available sizes, keys are size labels
   * @params from getters:
   * @param {Object<string>} computedSelectedSize: size label and skuId for
   *   selected sku, example: { label: 'M', value: '8949630003' }
   * @param {string} computedSelectedColor.code: color code for selected sku, e.g. "137603"
   * @return {Object} object with data representing the selected sku.
    */
  computedSelectedSku(state, getters) {
    const defaultSku = {
      color: {},
      size: {}
    };
    if (getters.isSizeSelected) {
      return state.product.bySize[getters.computedSelectedSize][getters.computedSelectedColor.code] || defaultSku;
    }
    if (state.product.displayOptions.showSkuDropDown && getters.skuDropdownLabels.length === 1) {
      return state.product.bySku[getters.skuDropdownLabels[0].value];
    }
    return defaultSku;
  },
  /**
   * getters.computedSizeChart
   *
   * Normalizing Size Chart Data for display
   * Used in both product-page (hidden) and size chart modal
   * TODO: Move to controller: PDM-3296
   *
   * @params from state.product:
   * @param {Object} sizeChart: size chart data
   * @param {Array} sizeChart.sizes are top level headers
   * @param {Array} sizeChart.rows are table rows containing objects with
   *   attribute: first column header
   *   values: array of cell values for that row
   */
  computedSizeChart(state) {
    const {
      sizeChart
    } = state.product;
    const addKeys = (label) => ({
      label,
      key: label.toLowerCase().replace(/\W/g, "")
    });
    if (!sizeChart || !sizeChart.sizes || !sizeChart.rows) return null;
    const sizes = sizeChart.sizes.map((size) => addKeys(size));
    const rows = sizeChart.rows.map((row) => {
      const attribute = addKeys(row.attribute);
      const values = row.values.map((value, index) => {
        const keyedValues = addKeys(value);
        return {
          label: keyedValues.label,
          key: `${attribute.key}-${index}`
        };
      });
      return {
        attribute,
        values
      };
    });
    const notes = sizeChart.notes ? sizeChart.notes.replace(/\r\n|\n|\r/gm, "<br /><br />") : null;
    return {
      title: sizeChart.title,
      sizes,
      rows,
      notes
    };
  },
  /**
   * getters.computedProductString
   *
   * Computing a product string to send with analytics click events. We build the string
   * progressively and include what data we have whenever the click takes place.
   * PDP Link Clicks Spec: https://rei-coop.atlassian.net/wiki/x/qIBz
   *
   * @params from state:
   * @param {Number} quantity: selected quantity
   * @param {String} product.styleId: product styleId
   * @param {Boolean} messages.promo.memberFirst: member first status
   * @params from getters:
   * @param {String} computedSelectedSku.skuId: selected sku ID
   * @param {Number} computedSelectedSku.price: selected sku price
   * @param {Boolean} computedIsMemberOnly: member only status
   * @param {String} computedSelectedShippingMethod: selected fulfillment method
   */
  computedProductString(state, getters) {
    var _a, _b;
    const {
      computedSelectedSku: {
        skuId,
        price
      },
      computedIsMemberOnly,
      computedSelectedShippingMethod
    } = getters;
    const {
      quantity,
      product: {
        styleId
      },
      messages: {
        promo: {
          memberFirst
        }
      },
      shipping: {
        shippingAddress: {
          postalCode
        }
      }
    } = state;
    const unitPrice = price ? price.price.value : "";
    const storeType = state.product.outlet ? "outlet" : "rei";
    const fulfillmentType = (_a = state.shipping.selectedStore) == null ? void 0 : _a.storeFulfillmentType;
    const shipTimestamp = state.shipping.shippingAvailability.estimatedArrivalTimestamp;
    const shipEad = shipTimestamp ? daysUntilNow(/* @__PURE__ */ new Date(), new Date(shipTimestamp)) : null;
    const pickTimestamp = (_b = state.shipping.selectedStore) == null ? void 0 : _b.estimatedArrivalTimestamp;
    const pickupEad = pickTimestamp ? daysUntilNow(/* @__PURE__ */ new Date(), new Date(pickTimestamp)) : null;
    const selectedEad = computedSelectedShippingMethod === "SHIP" ? shipEad : pickupEad;
    let products = `;${styleId};${quantity};${unitPrice};;eVar4=${computedSelectedShippingMethod}|eVar17=${storeType}`;
    if (skuId) products += `|eVar84=${skuId}`;
    if (selectedEad !== null) products += `|eVar83=${selectedEad}`;
    if (pickupEad !== null) products += `|eVar96=${pickupEad}`;
    if (shipEad !== null) products += `|eVar152=${shipEad}`;
    if (computedIsMemberOnly) products += "|eVar79=member only";
    if (memberFirst) products += "|eVar79=member first";
    if (fulfillmentType) products += `|eVar153=${fulfillmentType}`;
    if (postalCode) products += `|eVar155=${postalCode}`;
    return products;
  },
  /**
   * ! Controller Tickets Below this line ----------------------------
   */
  /**
   * getters.skuDropdownLabels
   *
   * @params from state.product.skus[]:
   * @param {string} size.name: e.g. "XS", "One Size"
   * @param {string} color.name: e.g. "BUFFALO GREEN BISON"
   * @param {number} price.price.value: current price for sku, e.g. 25
   * @param {number} skuId: 10 digit sku number, e.g. "1130750001"
   * @param {string} status: "AVAILABLE" or "UNAVAILABLE"
   *
   * @return {Array<Object>}: sku labels with properties
   *   label: formatted strings used by size/color combo box
   *     e.g. 'Crazytown Red/Nicholas Cage&nbsp;&nbsp;$50.00'
   *   value: skuId (used for ATC functionality)
   */
  skuDropdownLabels(state) {
    return state.product.skus.filter((sku) => sku.status !== "UNAVAILABLE" && sku.status !== "NOT_DISPLAYABLE" && sku.status !== "NOT_DISPLAYABLE").map((sku) => {
      const sizeName = sku.size.name !== "NONE" ? `/${toTitleCase(sku.size.name)}` : "";
      return {
        label: `${toTitleCase(sku.color.name)}${sizeName}&nbsp;&nbsp;$${sku.price.price.value.toFixed(2)}`,
        value: sku.skuId
      };
    });
  },
  /**
   * getters.isPreorder
   * @param {Boolean} state.product.allDisplayableSkusArePreorder all skus that are displayable
   *  (AVAILABLE, PREORDER, BACKORDER) are a PREORDER status
   * @param {Boolean} getters.isSkuSelected
   * @param {Boolean} getters.computedSelectedSku sku data object
   * @returns {Boolean} if the UI should display preorder messaging
   */
  isPreorder(state, getters) {
    if (getters.isSkuSelected) return getters.computedSelectedSku.status === "PRE_ORDER";
    return state.product.allDisplayableSkusArePreorder === true;
  },
  /**
   * getters.isSkuFirstView
   *
   * @param state
   * @property {object} state.viewedSkus - object whose property's key-value pair is a skuId-true.
   * Ex. { "9999999999": true, "9999998765": true, "9878887321": true }
   * @param getters
   * @property {boolean} getters.isSkuSelected
   * @property {string} getters.computedSelectedSku.skuId
   * @return {boolean} true if this the first time a customer is viewing the selected SKU
   */
  isSkuFirstView(state, getters) {
    return getters.isSkuSelected && !Object.hasOwn(state.viewedSkus, getters.computedSelectedSku.skuId);
  },
  /**
   * getters.sellableStatuses
   * @returns {Array<String>} list of statuses that can be sold/displayed on pdp
   */
  sellableStatuses() {
    return ["AVAILABLE", "PRE_ORDER", "BACKORDER"];
  },
  /**
   * getters.sellableSkus
   *
   * @param {Array<Object>} state.product.skus list of all possible skus for a style
   * @param {Array<String>} getters.sellableStatuses list of displayable/sellable statuses
   * @returns {Array<Object>} list of sku objects that have a sellable/displayable status
   */
  sellableSkus(state, getters) {
    return state.product.skus.filter((sku) => getters.sellableStatuses.includes(sku.status));
  },
  computedIsMemberOnly(state, getters) {
    if (getters.isSkuSelected) return getters.computedSelectedSku.membersOnly === true;
    return state.product.allSkusAreMembersOnly === true;
  },
  /**
   * getters.computedModalCloseLinkName
   *
   * @param {String} state.modalData.modalContent name of the current displayed modal
   * @returns {String} string required for the analytics click event dispatched
   * when a modal is closed. Most modals don't report this.
   */
  computedModalCloseLinkName(state) {
    if (state.modalData.modalContent === "StorePickupModal") return "modal_find a store_close";
    if (state.modalData.modalContent === "ProductReviewsReviewModal") return "reviews_close review";
    return "";
  },
  /**
   * getters.adobeABTestSegment
   *
   * translate data from adobe's target 'getOffer' method into identifiable string
   * following pattern of akamai service
   *
   * @param {Array} state.adobeTestData test data returned from https://experienceleague.adobe.com/docs/target/using/implement-target/client-side/at-js-implementation/functions-overview/adobe-target-getoffer.html?lang=en
   * response data from Adobe is an array of what adobe calls 'actions'
   *
   * 'content' parameter of Adobe action is arbitrary JSON defined in the test and target interface
   * https://experienceleague.adobe.com/docs/target/using/experiences/offers/create-json-offer.html?lang=en
   *
   * example data:
   * [
   *  {
   *    action: 'setJson'
   *    content: [{
   *      scope: 'corePDP',
   *      experience: 'variant'
   *      experimentName: 'pdpExperimentName',
   *      }
   *    ],
   *  },
   * ]
   * @returns {String} string with pattern "{experiment name}:{experiment version}"
   */
  adobeABTestSegment(state) {
    var _a;
    if (!Array.isArray(state.adobeTestData)) return "";
    const JSONExperiments = state.adobeTestData.filter((data) => data.action === "setJson")[0];
    const corePDPExperiment = (_a = JSONExperiments == null ? void 0 : JSONExperiments.content) == null ? void 0 : _a.filter((data) => data.scope === "corePDP")[0];
    if (!corePDPExperiment) return "";
    const {
      experimentName,
      experience
    } = corePDPExperiment;
    return `${experimentName}:${experience}`;
  },
  activeAdobeExperiments(state) {
    if (!Array.isArray(state == null ? void 0 : state.adobeTestData)) return [];
    return state.adobeTestData.reduce((acc, {
      action,
      content
    } = {}) => {
      if (action !== "setJson" || !Array.isArray(content)) return acc;
      content.forEach(({
        scope,
        experimentName,
        experience
      } = {}) => {
        if (scope === "corePDP" && (experimentName == null ? void 0 : experimentName.length) && (experience == null ? void 0 : experience.length)) acc.push(`${experimentName}:${experience}`);
      });
      return acc;
    }, []);
  },
  computedLoginPath(state, getters) {
    const query = getters.isSkuSelected ? `?sku=${getters.computedSelectedSku.skuId}` : "";
    return `${getters.appPropsMap.endpoints.loginPath}?toUrl=${state.product.canonicalUrl}${query}`;
  },
  isBike(state) {
    var _a, _b;
    return ((_b = (_a = state.product) == null ? void 0 : _a.taxCatRoot) == null ? void 0 : _b.findIndex(({
      canonical
    }) => canonical === "bikes")) !== -1;
  },
  isEBike(state) {
    var _a, _b;
    return ((_b = (_a = state.product) == null ? void 0 : _a.taxCatRoot) == null ? void 0 : _b.findIndex(({
      canonical
    }) => canonical === "electric-bikes")) !== -1;
  },
  bikeDiagram(state, getters) {
    return getters.appPropsMap.assets.bikeDiagram || "";
  },
  hasBikeSizeGuide(state, getters) {
    const {
      sizeChart
    } = state.product;
    return getters.isBike && !getters.isKidsBike && sizeChart !== void 0 && sizeChart !== null && Array.isArray(sizeChart.rows) && sizeChart.rows.find((row) => row.attribute === "Height Range") !== void 0;
  },
  isSleepingBag(state) {
    var _a, _b;
    return ((_b = (_a = state.product) == null ? void 0 : _a.taxCatRoot) == null ? void 0 : _b.findIndex(({
      canonical
    }) => canonical === "sleeping-bags")) !== -1;
  },
  /**
   * getters.showSleepingBagSizeChart
   * @param {Array} getters.sizeChartData.rows[0].values size chart height values
   * @param {Array} getters.sizeChartData.rows[1].values size chart hip values
   * @param {Array} getters.sizeChartData.sizes size chart size values
   * @returns {Boolean} if the sleeping bag size chart should be
   *  shown in place of the generic size chart
   */
  showSleepingBagSizeChart(state, getters) {
    var _a, _b, _c, _d;
    if (!getters.isSleepingBag || getters.sizeChartData === null) {
      return false;
    }
    const heights = (_b = (_a = getters.sizeChartData) == null ? void 0 : _a.rows[0]) == null ? void 0 : _b.values;
    const hips = (_d = (_c = getters.sizeChartData) == null ? void 0 : _c.rows[1]) == null ? void 0 : _d.values;
    const {
      sizes
    } = getters.sizeChartData;
    const isTableDataLengthEqual = (heights == null ? void 0 : heights.length) === (hips == null ? void 0 : hips.length) && (hips == null ? void 0 : hips.length) === (sizes == null ? void 0 : sizes.length);
    return isTableDataLengthEqual;
  },
  isFreeShipping(state, getters) {
    var _a;
    const {
      shippingThreshold
    } = getters.appPropsMap.textStrings;
    return (getters.computedPrice.minPrice.value >= shippingThreshold || ((_a = getters.unifiedCustomerData) == null ? void 0 : _a.isMember)) && !state.product.anyOversizeCharges;
  },
  /**
   * getters.availableColors
   * @param {State.product} product
   * @returns {Array} Array of only the available colors
   */
  // TODO deprecate this in favor of backend validation
  availableColors({
    product
  }) {
    return product.colors.filter((color) => {
      var _a, _b;
      return ((_b = (_a = product.byColor[color.code]) == null ? void 0 : _a.available) == null ? void 0 : _b.length) > 0;
    });
  },
  unifiedCustomerData(state) {
    const sessionData = state.user;
    const caas = state.customerAttributes;
    return {
      isCardHolder: !!(caas == null ? void 0 : caas.reiccholder),
      isLoggedIn: sessionData.isLoggedIn,
      isMember: !!sessionData.isMember || !!(caas == null ? void 0 : caas.member),
      preferredStore: sessionData.preferredStore
    };
  }
};
export {
  baseGetters as default
};
//# sourceMappingURL=baseGetters.mjs.map
